import { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, GripVertical, Loader2, Award, ImageIcon, Search } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { featuredProductsApi, productsApi, FeaturedProductItem } from '@/services/api';
import type { Product } from '@/types';
import { toast } from 'sonner';

interface SortableRowProps {
  item: FeaturedProductItem;
  index: number;
  saving: boolean;
  onRemove: (id: string) => void;
}

const SortableRow = ({ item, index, saving, onRemove }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50 bg-white">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
            disabled={saving}
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <span className="text-gray-500 font-medium w-6">{index + 1}</span>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          {item.product.image?.url ? (
            <img
              src={item.product.image?.url}
              alt={item.product.title}
              className="w-12 h-12 object-cover rounded-lg"
            />
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">{item.product.title}</div>
            <div className="text-sm text-gray-500">{item.product.slug}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {item.product.categories && item.product.categories.length > 0 ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {item.product.categories[0].name}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <button
          onClick={() => onRemove(item.id)}
          disabled={saving}
          className="text-red-600 hover:text-red-800 disabled:opacity-50"
          title="Kaldır"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </td>
    </tr>
  );
};

export const FeaturedProductsPage = () => {
  const [featuredProducts, setFeaturedProducts] = useState<FeaturedProductItem[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Load both in parallel - use getVisible (public) for products
      const [featured, productsResponse] = await Promise.all([
        featuredProductsApi.getAll(),
        productsApi.getVisible({ limit: 200 }),
      ]);

      setFeaturedProducts(featured || []);

      // Get products array from paginated response
      const products = productsResponse?.data || [];
      setAllProducts(products);

    } catch (err: any) {
      console.error('Veri yüklenemedi:', err);
      setError(err.message || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get available products (not already featured) and filter by search
  const availableProducts = allProducts.filter(
    product => !featuredProducts.some(fp => fp.productId === product.id)
  );

  const filteredProducts = availableProducts.filter(product =>
    product.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddFeatured = async () => {
    if (!selectedProductId) return;

    try {
      setSaving(true);
      setError('');
      await featuredProductsApi.add(selectedProductId);
      toast.success('Ürün öne çıkarıldı');
      setModalOpen(false);
      setSelectedProductId('');
      await loadData();
    } catch (err: any) {
      console.error('Add featured error:', err);
      setError(err.message || 'Ürün öne çıkarılamadı');
      toast.error(err.message || 'Ürün öne çıkarılamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Bu ürünü öne çıkanlardan kaldırmak istediğinize emin misiniz?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await featuredProductsApi.remove(id);
      toast.success('Ürün öne çıkanlardan kaldırıldı');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız');
      toast.error(err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = featuredProducts.findIndex((item) => item.id === active.id);
    const newIndex = featuredProducts.findIndex((item) => item.id === over.id);

    // Optimistic update
    const newOrder = arrayMove(featuredProducts, oldIndex, newIndex);
    setFeaturedProducts(newOrder);

    try {
      setSaving(true);
      setError('');
      // Send bulk update with new order
      const productIds = newOrder.map((item) => item.productId);
      await featuredProductsApi.bulkUpdate(productIds);
    } catch (err: any) {
      setError(err.message || 'Sıralama güncellenemedi');
      toast.error(err.message || 'Sıralama güncellenemedi');
      // Revert on error
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Öne Çıkan Ürünler</h1>
          <p className="text-gray-600 mt-1">
            Ana sayfada görünecek ürünleri seçin ve sıralayın
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          disabled={availableProducts.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          Ürün Ekle
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Featured Products List */}
      {featuredProducts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Award className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Henüz öne çıkan ürün yok
          </h3>
          <p className="text-gray-600 mb-4">
            Ana sayfada gösterilecek ürünleri ekleyin
          </p>
          <button
            onClick={() => setModalOpen(true)}
            disabled={availableProducts.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <Plus className="w-5 h-5" />
            Ürün Ekle
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sıra
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ürün
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <SortableContext
                  items={featuredProducts.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {featuredProducts.map((item, index) => (
                    <SortableRow
                      key={item.id}
                      item={item}
                      index={index}
                      saving={saving}
                      onRemove={handleRemove}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>
      )}

      {/* Add Product Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Ürün Öne Çıkar</h2>
              <button
                onClick={() => {
                  setModalOpen(false);
                  setSelectedProductId('');
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-4 flex-1 overflow-hidden flex flex-col">
              {availableProducts.length === 0 ? (
                <p className="text-gray-600 text-center py-4">
                  Tüm ürünler zaten öne çıkarılmış.
                </p>
              ) : (
                <div className="space-y-4 flex-1 flex flex-col overflow-hidden">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Ürün ara..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                    />
                  </div>

                  {/* Product List */}
                  <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg max-h-[300px]">
                    {filteredProducts.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        {searchQuery ? 'Ürün bulunamadı' : 'Ürün yok'}
                      </p>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredProducts.map((product) => (
                          <button
                            key={product.id}
                            onClick={() => setSelectedProductId(product.id)}
                            className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors ${
                              selectedProductId === product.id ? 'bg-gray-100' : ''
                            }`}
                          >
                            {product.images?.[0]?.url ? (
                              <img
                                src={product.images[0].url}
                                alt={product.title}
                                className="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                            <span className="font-medium text-gray-900 truncate">
                              {product.title}
                            </span>
                            {selectedProductId === product.id && (
                              <span className="ml-auto text-black">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      onClick={() => {
                        setModalOpen(false);
                        setSelectedProductId('');
                        setSearchQuery('');
                      }}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleAddFeatured}
                      disabled={!selectedProductId || saving}
                      className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                      Ekle
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedProductsPage;
